import { useEffect, useId, useRef } from 'react';
import { Box, type BoxProps } from '@nimbus-ds/components';

type PandaPlayerEmbedProps = Readonly<{
  videoId: string;
  onReady?: () => void;
  onError?: (error: unknown) => void;
  readyTimeoutMs?: number;
  boxProps?: BoxProps;
  autoPlay?: boolean;
}>;

declare global {
  // Expose SDK globals on the universal global object
  // eslint-disable-next-line no-var
  var PandaPlayer:
    | (new (
        containerId: string,
        options: {
          onReady?: () => void;
          onError?: (err: unknown) => void;
          video_id?: string;
          library_id?: string;
          defaultStyle?: boolean;
          playerConfigs?: Record<string, unknown>;
        },
      ) => {
        play: () => void;
        pause: () => void;
        destroy: () => void;
        onEvent?: (cb: (payload: { message: string }) => void) => void;
      })
    | undefined;
  // eslint-disable-next-line no-var
  var pandascripttag: Array<() => void> | undefined;
}

const PANDA_SDK_SRC = 'https://player.pandavideo.com.br/api.v2.js';
const PANDA_LIBRARY_ID = 'vz-fa3b219e-70a' as const;

function getPandaQueue(): Array<() => void> {
  if (!globalThis.pandascripttag) globalThis.pandascripttag = [];
  return globalThis.pandascripttag;
}

function ensurePandaSdk(): Promise<void> {
  if (typeof document === 'undefined') return Promise.resolve();
  if (globalThis.PandaPlayer) return Promise.resolve();
  getPandaQueue();

  const existing = document.querySelector<HTMLScriptElement>(
    `script[src="${PANDA_SDK_SRC}"]`,
  );
  if (existing) {
    return new Promise((resolve) => {
      const check = () =>
        globalThis.PandaPlayer ? resolve() : setTimeout(check, 50);
      check();
    });
  }

  return new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.async = true;
    s.src = PANDA_SDK_SRC;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('Failed to load Panda Player SDK'));
    document.body.appendChild(s);
  });
}

export function PandaPlayerEmbed({
  videoId,
  onReady,
  onError,
  readyTimeoutMs: _readyTimeoutMs = 10000,
  boxProps,
  autoPlay,
}: PandaPlayerEmbedProps) {
  const containerId = useId().replaceAll(':', '-');
  const playerRef = useRef<{ destroy: () => void } | null>(null);
  const initializedRef = useRef(false);
  const onReadyRef = useRef<PandaPlayerEmbedProps['onReady']>(onReady);
  const onErrorRef = useRef<PandaPlayerEmbedProps['onError']>(onError);

  useEffect(() => {
    onReadyRef.current = onReady;
  }, [onReady]);
  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);

  useEffect(() => {
    let cancelled = false;

    if (!videoId) {
      onErrorRef.current?.(new Error('Missing videoId'));
      return () => undefined;
    }

    if (!PANDA_LIBRARY_ID) {
      onErrorRef.current?.(new Error('Missing library_id'));
      return () => undefined;
    }

    const instantiate = () => {
      const PandaPlayerCtor = globalThis.PandaPlayer;
      if (!PandaPlayerCtor || cancelled) return;
      if (initializedRef.current) return;
      try {
        const options = {
          video_id: videoId,
          library_id: PANDA_LIBRARY_ID,
          defaultStyle: true,
          playerConfigs: { autoplay: '1' },
          onReady: () => {
            if (cancelled) return;
            if (autoPlay) {
              try {
                // Some browsers may block autoplay; intentionally ignore errors
                // eslint-disable-next-line @typescript-eslint/no-unused-expressions
                (player as unknown as { play: () => void }).play?.();
              } catch {
                // ignore autoplay errors
              }
            }
            onReadyRef.current?.();
          },
          onError: (err: unknown) => {
            if (cancelled) return;
            onErrorRef.current?.(err);
          },
        } as const;

        const player = new PandaPlayerCtor(containerId, options);
        playerRef.current = player;
        initializedRef.current = true;
      } catch (e) {
        onErrorRef.current?.(e);
      }
    };

    ensurePandaSdk()
      .then(() => {
        if (cancelled) return;
        if (globalThis.PandaPlayer) {
          instantiate();
          return;
        }
        const queue = getPandaQueue();
        queue.push(instantiate);
      })
      .catch((e) => {
        onErrorRef.current?.(e);
      });

    return () => {
      cancelled = true;
      try {
        playerRef.current?.destroy();
      } catch {
        // noop
      }
      playerRef.current = null;
      initializedRef.current = false;
    };
  }, [containerId, videoId, autoPlay]);

  return (
    <Box
      width="100%"
      maxWidth="632px"
      borderRadius="2"
      overflow="hidden"
      position="relative"
      {...boxProps}
    >
      <div id={containerId} style={{ position: 'absolute', inset: 0 }} />
    </Box>
  );
}
